import React, { useState, useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';

// Store
import { RootState, Dispatch } from '../../store/store';

// Enums
import Order from '../../enums/order.enum';

// Utils
import getValidTemperature from '../../utils/getValidTemperature';
import getValidDeltaTemperature from '../../utils/getValidDeltaTemperature';
import getMeasurementPreference from '../../utils/getMeasurementPreference';

// Components
import TableTh from '../TableTh/TableTh';

import getOptions, { Option } from './option';
import CycleStatusEnum from '../../enums/cycle-status.enum';
import Modal from '../Modal/Modal';
import CyclePackage from '../CyclePackage/CyclePackage';
import { AbilityContext } from '../Can/Can';
import getValidCycleState from '../../utils/getValidCycleState';
import ScreenCycleTableRow from './ScreenCycleTableRow';
import IconPlay from '../Icons/IconPlay/IconPlay';
import IconPaused from '../Icons/IconPaused/IconPaused';
import IconCrossPaused from '../Icons/IconCrossPaused/IconCrossPaused';
import IconStop from '../Icons/IconStop/IconStop';
import IconGearFilled from '../Icons/IconGearFilled/IconGear';
import { ElementSize } from '../../types/element-size';
import { SKIP_SYMBOL } from '../CycleTable/constants';
import { Action } from '../../enums/action';
import { PAGE_TITLE } from '../../constants/common';
import { CycleHighestMetrics, ScreenCyclesTableBaseProps, ScreenCycleWithId, SortScreenCyclesTable } from './types/screen-cycle-table';
import { getCycleIotSummary } from './utils/getCycleIotSummary';
import { useTranslation } from 'react-i18next';
import { CELSIUS_TEMPERATURE_DIFF_THRESHOLD } from './utils/getTemperatureBorderClass';
import { DateFormats } from '../../enums/date-formats';
import { DateFormatter } from '../../utils/date-formatter';
import { useCalCertGuardedAction } from '../../hooks/useCalCertGuardedAction';
import CalCertWarningModal from '../CalCertWarningModal/CalCertWarningModal';

type ScreenCyclesTableProps = ScreenCyclesTableBaseProps & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ScreenCyclesTable: React.FC<ScreenCyclesTableProps> = ({
  screenCycles,
  measurement: reduxMeasurement,
  measurementOverride,
  actionsButtons,
  status,
  pinnedActionsColumnLabel,
  savePackage,
  getPackage,
  startCycle,
  stopCycle,
  pauseCycle,
  unpauseCycle,
  openSettings,
  screenCriticalDeviation = CELSIUS_TEMPERATURE_DIFF_THRESHOLD,
  resetCyclePackage,
  dragContractId,
  dragScreenId,
  updateController,
  user,
}) => {
  const measurement = measurementOverride ?? reduxMeasurement;
  const ability = useContext(AbilityContext);
  const { t } = useTranslation();

  const {
    run: runCalCertGuardedAction,
    missingControllers: missingCalCertControllers,
    closeModal: closeCalCertModal,
    disableCheckAndRetry: disableCalCertCheckAndRetry,
    proceedWithoutCalCert,
  } = useCalCertGuardedAction(updateController);

  const canEditCycle = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE);
  }, [ability]);

  const [selectedCycle, setSelectedCycle] = useState<ScreenCycleWithId | null>(null);
  const [cyclePackageSaving, setCyclePackageSaving] = useState<boolean>(false);
  const [selectedCyclePackage, setSelectedCyclePackage] = useState<File | null>(null);
  const [isPackageModalOpen, setIsPackageModalOpen] = useState<boolean>(false);

  const getCycleActions = (cycle: ScreenCycleWithId) => {
    return [
      {
        name: t('commonText.play'),
        icon: <IconPlay size={ElementSize.XXS} iconClassName='py-1' />,
        onClick: () =>
          runCalCertGuardedAction((options) => startCycle({ id: cycle.id, skipCalCertCheck: options?.skipCalCertCheck })),
      },
      {
        name: t('commonText.pause'),
        icon: <IconPaused size={ElementSize.XXS} iconClassName='py-1' />,
        onClick: () => pauseCycle(cycle.id),
      },
      {
        name: t('commonText.unpause'),
        icon: <IconCrossPaused size={ElementSize.XXS} iconClassName='py-1' width={24} />,
        onClick: () => unpauseCycle(cycle.id),
      },
      {
        name: t('commonText.stop'),
        icon: <IconStop size={ElementSize.XXS} iconClassName='py-1' />,
        onClick: () => stopCycle(cycle.id),
      },
      {
        name: t('commonText.cycleSettings'),
        icon: <IconGearFilled size={ElementSize.XXS} iconClassName='py-1' />,
        onClick: () => openSettings(cycle),
      },
    ];
  }

  const saveCyclePackage = async () => {
    if (selectedCyclePackage && selectedCycle) {
      setCyclePackageSaving(true);
      try {
        await savePackage({ id: selectedCycle.id, cyclePackage: selectedCyclePackage });
      } finally {
        setCyclePackageSaving(false);
      }
    }
  };

  const onPackageModalClose = () => {
    setSelectedCyclePackage(null);
    setIsPackageModalOpen(false);
    resetCyclePackage();
  }

  const processCycles = (cycles: ScreenCycleWithId[]): CycleHighestMetrics[] => {
    return cycles.map((cycle) => {
      const {
        highestTemp,
        lowestTemp,
        highestSp,
        highestPresent,
        lowestPresent,
        minDeltaT,
        maxDeltaT,
        highestState,
        step,
        timeLeft,
      } = getCycleIotSummary(cycle);

      const isChannelExist = Boolean(highestState);
      const isActiveCycle = cycle.status === CycleStatusEnum.Active

      const actions = (isChannelExist && isActiveCycle) ? getCycleActions(cycle) : SKIP_SYMBOL;

      return {
        id: cycle.id,
        name: cycle.name,
        completed_date: DateFormatter({ date: cycle.end_at, format: DateFormats['dd-MMM yyyy HH:mm:ss a'] }),
        cycle_device_actions: actions,
        highest_temp: getValidTemperature(highestTemp, measurement),
        lowest_temp: getValidTemperature(lowestTemp, measurement),
        highest_sp: getValidTemperature(highestSp, measurement),
        highest_present: highestPresent,
        lowest_present: lowestPresent,
        min_delta_t: getValidDeltaTemperature(minDeltaT, measurement),
        max_delta_t: getValidDeltaTemperature(maxDeltaT, measurement),
        highest_state: getValidCycleState(highestState),
        data: cycle,
        step,
        time_left: timeLeft,
      };
    });
  };

  const getAvailableOptions = (cycleStatus: CycleStatusEnum, option: Option): Partial<Option> => {
    if (cycleStatus === CycleStatusEnum.InActive) {
      return {
        NAME: option.NAME,
        ACTIONS: option.ACTIONS,
      };
    }

    if (cycleStatus === CycleStatusEnum.Completed) {
      return {
        NAME: option.NAME,
        COMPLETED_DATE: option.COMPLETED_DATE,
        ACTIONS: option.ACTIONS,
      };
    }

    if (!canEditCycle) {
      delete option.CYCLE_DEVICE_ACTIONS;
      delete option.COMPLETED_DATE;
      return option;
    }

    delete option.COMPLETED_DATE;
    return option;
  }

  const tableBody = processCycles(screenCycles);

  const fullOptions = getOptions(measurement);

  const cycleOption = getAvailableOptions(status, fullOptions);

  const hiddenColumns = user?.hidden_cycle_table_columns ?? [];

  const visibleCycleOption: Partial<Option> = Object.fromEntries(
    Object.entries(cycleOption).filter(([key]) => !hiddenColumns.includes(key)),
  );

  const DEFAULT_SORT: SortScreenCyclesTable = { key: String(cycleOption.NAME.KEY), option: Order.ASC }

  const [sort, setSort] = useState<SortScreenCyclesTable>(DEFAULT_SORT);

  const changeSort = (optionKey: string | null, sortOption: SortScreenCyclesTable) => {
    if (sortOption.key !== optionKey) {
      setSort({ key: optionKey, option: Order.ASC });
      return;
    }

    if (sortOption.key === optionKey) {
      const newOption = sortOption.option === Order.ASC ? Order.DESC : Order.ASC;
      setSort({ key: optionKey, option: newOption });
      return;
    }
  };

  const sortArray = (array: CycleHighestMetrics[], sortOption: SortScreenCyclesTable): CycleHighestMetrics[] => {
    const { key, option } = sortOption;

    return array.slice().sort((a, b) => {
      let valueA = a[key];
      let valueB = b[key];

      if (key === 'completed_date') {
        valueA = a.data.end_at;
        valueB = b.data.end_at;
      }

      const orderMultiplier = option === Order.ASC ? 1 : -1;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return (valueA - valueB) * orderMultiplier;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const isDateA = !isNaN(Date.parse(valueA));
        const isDateB = !isNaN(Date.parse(valueB));

        if (isDateA && isDateB) {
          return (
            (new Date(valueA).getTime() - new Date(valueB).getTime()) * orderMultiplier
          );
        }

        return valueA.localeCompare(valueB, undefined, { numeric: true }) * orderMultiplier;
      }


      return 0;
    });
  };

  useEffect(() => {
    if (isPackageModalOpen && selectedCycle) {
      getPackage(selectedCycle.id);
    }
  }, [isPackageModalOpen, selectedCycle]);

  const hasPinnedAction = actionsButtons.some((action) => action.isPinned);

  return (
    <>
      <div className="c-screen-cycles-table text-center font-medium overflow-x-auto">
        <table className="c-screen-cycles-table__table w-full">
          <thead className="c-screen-cycles-table__thead text-nowrap text-xs leading-4 text-slate-gray dark:text-white">
            <tr className="c-screen-cycles-table__tr-head">
              {hasPinnedAction && (
                <th className="c-table-th sticky top-0 z-10 min-w-[46px] bg-white dark:bg-boxdark">
                  {pinnedActionsColumnLabel && (
                    <div className="flex flex-row flex-nowrap justify-center">{pinnedActionsColumnLabel}</div>
                  )}
                </th>
              )}
              {Object.keys(visibleCycleOption).map((optionKey) => (
                <TableTh
                  key={optionKey}
                  classNameTh={`${visibleCycleOption[optionKey].BLOCK_PARAMETERS} sticky top-0 z-10 bg-white dark:bg-boxdark`}
                  classNameBlock={`justify-${visibleCycleOption[optionKey].ALIGNMENT}`}
                  option={visibleCycleOption[optionKey]}
                  dataSort={sort}
                  onClick={changeSort} />
              ))}
            </tr>
          </thead>
          <tbody className="c-screen-cycles-table__tbody max-md:text-xs max-md:leading-6 whitespace-nowrap">
            {sortArray(tableBody, sort).map((cycle, index) => (
              <ScreenCycleTableRow
                key={cycle.id}
                cycleOption={visibleCycleOption}
                actionsButtons={actionsButtons}
                cycle={cycle}
                index={index}
                setSelectedCycle={setSelectedCycle}
                setIsPackageModalOpen={setIsPackageModalOpen}
                ability={ability}
                criticalDeviation={screenCriticalDeviation}
                dragContractId={dragContractId}
                dragScreenId={dragScreenId}
              />
            ))}
          </tbody>
        </table>
      </div>
      <Modal
        modalTitle="Cycle Package"
        isVisible={isPackageModalOpen}
        onClose={onPackageModalClose}
        onAccept={saveCyclePackage}
        acceptBtnText={`${cyclePackageSaving ? 'Saving...' : 'Save'}`}
        className="min-h-[500px] w-[800px]"
      >
        <CyclePackage onSelectFile={setSelectedCyclePackage} cycleName={selectedCycle?.name || ''} />
      </Modal>
      {missingCalCertControllers && (
        <CalCertWarningModal
          isVisible
          controllers={missingCalCertControllers}
          onClose={closeCalCertModal}
          onDisableCheck={disableCalCertCheckAndRetry}
          onProceedWithoutCalCert={proceedWithoutCalCert}
          proceedButtonText="Start without Cal-Cert"
        />
      )}
    </>
  )
};

const mapState = (state: RootState) => ({
  measurement: getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
  cycle: state.cycles.cycle,
  user: state.user.profile,
});

const mapDispatch = (dispatch: Dispatch) => ({
  savePackage: dispatch.cycles.savePackage,
  getPackage: dispatch.cycles.getPackage,
  startCycle: dispatch.cycles.start,
  stopCycle: dispatch.cycles.stop,
  pauseCycle: dispatch.cycles.pause,
  unpauseCycle: dispatch.cycles.unpause,
  resetCyclePackage: dispatch.cycles.resetCyclePackage,
  updateController: dispatch.controllers.updateController,
});

export default connect(mapState, mapDispatch)(ScreenCyclesTable);